import { useSortable } from '@dnd-kit/sortable';
import {
  createColumnHelper,
  type AccessorFnColumnDef,
  type RowData,
} from '@tanstack/react-table';
import { clsx } from 'clsx';
import { type JSX } from 'react';
import { useIntl } from 'react-intl';

import { DragAllDirectionIcon } from '@dynatrace/strato-icons';

import * as styles from './RowOrder.sty.js';
import { Button } from '../../../../buttons/button/Button.js';
import { useModality } from '../../../../core/hooks/useModality.js';
import { focusRingStatic } from '../../../../core/styles/focusRingStatic.sty.js';
import { DataTableDefaultCell } from '../../components/DataTableDefaultCell.js';
import {
  DATA_TABLE_BUILTIN_COLUMN_MIN_WIDTH,
  DATA_TABLE_ROW_DRAG_COLUMN_ID,
} from '../../constants.js';
import { dataTableMessages } from '../../messages.js';
import type { TDataTableFeatures } from '../types/features.types.js';

const rowOrderColumnHelper = createColumnHelper<TDataTableFeatures, RowData>();

const RowDragHandleCell = ({
  rowId,
  disabled,
  isDraggingActive,
}: {
  rowId: string;
  disabled: boolean;
  isDraggingActive: boolean;
}): JSX.Element => {
  const intl = useIntl();
  const modality = useModality();
  const { attributes, listeners } = useSortable({
    id: rowId,
  });

  const isKeyboardDragging = isDraggingActive && modality === 'keyboard';
  const rowDragHandleLabel = intl.formatMessage(
    dataTableMessages.rowDragAndDropLabel,
    { rowId },
  );

  return (
    <DataTableDefaultCell>
      <Button
        size="condensed"
        variant="default"
        {...attributes}
        {...(disabled ? undefined : listeners)}
        aria-disabled={disabled}
        aria-label={rowDragHandleLabel}
        className={clsx(
          styles.dndHandle({
            disabled,
          }),
          // Apply the focus ring unconditionally (when: 'always')
          // to the dragged row's handle during keyboard dragging.
          isKeyboardDragging &&
            focusRingStatic({
              minimal: false,
              color: 'neutral',
              when: 'always',
            }),
        )}
        data-dt-name="RowOrderDragAndDrop"
      >
        <Button.Prefix>
          <DragAllDirectionIcon />
        </Button.Prefix>
      </Button>
    </DataTableDefaultCell>
  );
};

/**
 * Column definition for the row drag column.
 * @internal
 */
export const ROW_DRAG_COLUMN: AccessorFnColumnDef<
  TDataTableFeatures,
  RowData,
  string
> = rowOrderColumnHelper.accessor(() => '', {
  id: DATA_TABLE_ROW_DRAG_COLUMN_ID,
  alignment: 'center',
  header: () => {
    return '';
  },
  width: 'content',
  minWidth: DATA_TABLE_BUILTIN_COLUMN_MIN_WIDTH,
  enableSorting: false,
  enableResizing: false,
  enableUserActions: false,
  enableHiding: false,
  meta: {
    builtin: true,
    hasCustomCellRenderer: true,
    hasCustomHeaderRenderer: true,
    builtinPlacement: 'prefix',
    cellDeps: (cell) => {
      return [cell.row.getCanDrag()];
    },
  },
  cell: ({ row, table }) => (
    <RowDragHandleCell
      rowId={row.id}
      disabled={!row.getCanDrag()}
      isDraggingActive={table.atoms.draggingRowInfo?.get()?.id === row.id}
    />
  ),
});
